import { Dialog } from '@material-ui/core';
import React, { Dispatch, SetStateAction } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import {
  ITargetingPreset,
  patchTargetingPreset,
} from '~/src/dash/apiV2/targeting/targetingPresets';
import { CloseButton } from '~/src/dash/components/buttons/commonButtons/CloseButton';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import { ButtonV2, Col, Cols, Row, Rows, spacing, Text } from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';
import { targetingPresetFormConfig } from '~/src/dash/views/tools/TargetingPresets/TargetingPresetFormView/config';

interface IAssociatedItemsProps {
  label: string;
  singularLabel: string;
  iconName: string;
  numItems: number;
}

const AssociatedItem = ({ label, singularLabel, iconName, numItems }: IAssociatedItemsProps) => {
  const correctLabel = numItems === 1 ? singularLabel : label;
  return (
    <Cols gap={spacing.small} style={{ alignItems: 'center' }}>
      <Col displayFlex>
        <IconV2 name={iconName} small />
      </Col>
      <Col>
        <Text small>{`${numItems} ${correctLabel}`}</Text>
      </Col>
    </Cols>
  );
};

const extractNumItems = (
  label: 'Bidders' | 'Campaigns',
  numBidders: number = 0,
  numCampaigns: number = 0,
) => {
  switch (label) {
    case 'Bidders':
      return numBidders;
    case 'Campaigns':
      return numCampaigns;
    default:
      return 0;
  }
};

interface IProps {
  preset: Partial<ITargetingPreset>;
  handleSuccess: (preset: ITargetingPreset) => void;
  open: boolean;
  handleClose: () => void;
  setSnackbarError: Dispatch<SetStateAction<boolean>>;
  setSnackbarMessage: React.Dispatch<React.SetStateAction<string>>;
  publisherID: string;
  numBidders: number;
  numCampaigns: number;
}

export const UpdateTargetingPresetDialog = ({
  open,
  preset,
  handleSuccess,
  handleClose,
  setSnackbarError,
  setSnackbarMessage,
  publisherID,
  numBidders,
  numCampaigns,
}: IProps) => {
  const handleUpdate = () => {
    patchTargetingPreset(publisherID, preset)
      .then((res) => {
        if (res.Preset) {
          handleSuccess(res.Preset);
        } else {
          unstable_batchedUpdates(() => {
            setSnackbarMessage('An error has occurred.');
            setSnackbarError(true);
          });
          handleClose();
        }
      })
      .catch((err) => {
        console.error(err);
        unstable_batchedUpdates(() => {
          setSnackbarMessage('An error has occurred. Please check your values and try again.');
          setSnackbarError(true);
        });
        handleClose();
      });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby='alert-dialog-update-preset'
      aria-describedby='alert-dialog-update-preset'
    >
      <Rows largePadding gap={spacing.base} style={{ minWidth: '600px' }}>
        <Row noPadding>
          <Cols style={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <Col>
              <Text bold>Confirm Save</Text>
            </Col>
            <Col>
              <CloseButton onClick={handleClose} />
            </Col>
          </Cols>
        </Row>
        <Row noPadding>
          <Cols
            gap={spacing.small}
            style={{
              alignItems: 'center',
              backgroundColor: publicaColors.honeydew,
              padding: '2px 8px',
            }}
          >
            <Col displayFlex>
              <IconV2 name='all-done' small />
            </Col>
            <Col>
              <Text small>
                Our targeting validation algorithm detected no conflicts in the latest edits.
              </Text>
            </Col>
          </Cols>
        </Row>
        <Row noPadding>
          <Text small bold>
            Do you confirm wanting to update targeting the following items?
          </Text>
        </Row>
        <Row noPadding>
          <Cols paddingLeft={spacing.base} gap={spacing.large} style={{ alignItems: 'center' }}>
            {targetingPresetFormConfig.badges.map(({ pluralLabel, singularLabel, iconName }) => {
              return (
                <Col>
                  <AssociatedItem
                    label={pluralLabel}
                    singularLabel={singularLabel}
                    iconName={iconName}
                    numItems={extractNumItems(pluralLabel, numBidders, numCampaigns)}
                  />
                </Col>
              );
            })}
          </Cols>
        </Row>
        <Row noPadding>
          <Cols gap={spacing.large} style={{ justifyContent: 'flex-end', alignItems: 'center' }}>
            <Col>
              <ButtonV2 small onClick={handleUpdate}>
                Confirm & Save
              </ButtonV2>
            </Col>
            <Col>
              <ButtonV2 small clearBackground onClick={handleClose} color={publicaColors.lightBlue}>
                Cancel
              </ButtonV2>
            </Col>
          </Cols>
        </Row>
      </Rows>
    </Dialog>
  );
};
